// src/pages/auth/ResetPassword.tsx
import { logger } from '../../utils/logger';

import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonSpinner,
  IonIcon,
  IonToast,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import './Auth.css';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');
  const history = useHistory();

  useEffect(() => {
    // Check if we have a recovery token stored
    const checkRecoveryToken = () => {
      const recoveryToken = sessionStorage.getItem('dicecat_recovery_token');
      if (!recoveryToken) {
        // No token means the recovery link might be expired or user navigated here directly
        setError('Password reset link has expired or is invalid. Please request a new one.');
        setTimeout(() => {
          history.replace('/login');
        }, 3000);
      }
    };
    checkRecoveryToken();
  }, [history]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Get the recovery token from sessionStorage
      const recoveryToken = sessionStorage.getItem('dicecat_recovery_token');
      const refreshToken = sessionStorage.getItem('dicecat_recovery_refresh_token');

      if (!recoveryToken) {
        throw new Error('Recovery token not found. Please request a new password reset link.');
      }

      // Create a temporary session with the recovery token ONLY for updating password
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: recoveryToken,
        refresh_token: refreshToken || '',
      });

      if (sessionError) throw sessionError;

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      // Clear recovery tokens from sessionStorage
      sessionStorage.removeItem('dicecat_recovery_token');
      sessionStorage.removeItem('dicecat_recovery_refresh_token');

      setToastMessage('Password updated successfully! Redirecting to login...');
      setToastColor('success');
      setShowToast(true);

      // Sign out and redirect to login after success
      setTimeout(async () => {
        await supabase.auth.signOut();
        history.replace('/login');
      }, 2000);
    } catch (error: any) {
      logger.error('Password reset error:', error);
      setError(error.message || 'Failed to reset password');
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="auth-container">
          {/* Logo and Branding */}
          <div className="dicecat-branding">
            <img
              src="/assets/dicecat-logo-composite.svg"
              alt="Dice Cat"
              className="dicecat-composite-logo"
            />
            <p className="dicecat-tagline">Connect with board gamers and RPG players online and near you</p>
          </div>

          {/* Header */}
          <div className="auth-header">
            <h2>Reset Your Password</h2>
            <p>Enter your new password below</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <IonItem>
              <IonLabel position="stacked">New Password</IonLabel>
              <IonInput
                type={showPassword ? 'text' : 'password'}
                value={password}
                onIonInput={(e) => setPassword(e.detail.value!)}
                required
                placeholder="At least 6 characters"
                autocomplete="new-password"
              />
              <IonIcon
                slot="end"
                icon={showPassword ? eyeOffOutline : eyeOutline}
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--ion-color-medium)',
                  marginTop: '20px'
                }}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Confirm New Password</IonLabel>
              <IonInput
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                required
                placeholder="Re-enter your password"
                autocomplete="new-password"
              />
              <IonIcon
                slot="end"
                icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--ion-color-medium)',
                  marginTop: '20px'
                }}
              />
            </IonItem>

            {error && (
              <IonText color="danger">
                <p className="error-text">{error}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              disabled={loading}
              className="ion-margin-top"
            >
              {loading ? <IonSpinner name="crescent" /> : 'Reset Password'}
            </IonButton>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            <IonButton
              expand="block"
              fill="clear"
              routerLink="/login"
              disabled={loading}
            >
              Back to Login
            </IonButton>
          </div>
        </div>

        {/* Toast for feedback */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="bottom"
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default ResetPassword;
